import { Download } from 'lucide-react'
import { Button, Dropdown, SearchInput } from '../../../../components'
import { CustomDatePicker } from '../../../../components/CustomDatePicker'
import type { DropdownOption } from '../../../../components/Dropdown'
import { ATTENDANCE_STATUS_OPTIONS } from '../constants/hrmsAttendanceRecords.constants'
import type { StaffAttendanceSummary } from '../types/hrmsAttendanceRecords.types'

interface AttendanceFiltersProps {
  searchQuery: string
  onSearchChange: (val: string) => void
  selectedDate: string
  onDateChange: (val: string) => void
  selectedDepartment: string
  onDepartmentChange: (val: string) => void
  departmentOptions: DropdownOption[]
  statusFilter: string
  onStatusFilterChange: (val: string) => void
  onClearFilters: () => void
  onExport: () => void
  isExporting?: boolean
  summary?: StaffAttendanceSummary
}

export function AttendanceFilters({
  searchQuery,
  onSearchChange,
  selectedDate,
  onDateChange,
  selectedDepartment,
  onDepartmentChange,
  departmentOptions,
  statusFilter,
  onStatusFilterChange,
  onClearFilters,
  onExport,
  isExporting = false,
  summary,
}: AttendanceFiltersProps) {
  const presentCount = summary?.present ?? 0
  const lateCount = summary?.late ?? 0
  const absentCount = summary?.absent ?? 0

  const statusOptions = ATTENDANCE_STATUS_OPTIONS.map((opt) => ({
    label: opt.value === 'All Statuses' ? 'Status: All' : opt.label,
    value: opt.value,
  }))

  const effectiveDeptValue =
    selectedDepartment === 'All Departments' ? '' : selectedDepartment || ''

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs space-y-3 min-w-0 font-sans">
      {/* Top Filter Row: Search bar -> Department dropdown -> Status dropdown -> Date picker -> Clear Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search Input Control */}
        <div className="h-11 flex-none w-[300px] [&>*]:h-full [&_input]:h-full">
          <SearchInput
            placeholder="Search employee..."
            value={searchQuery}
            onChange={onSearchChange}
            onClear={() => onSearchChange('')}
            className="h-full w-full"
          />
        </div>

        {/* Department Dropdown Control */}
        <div className="h-11 w-full sm:w-[180px] flex-none [&>*]:h-full [&_button]:h-full [&_button]:!rounded-xl">
          <Dropdown
            options={departmentOptions}
            value={effectiveDeptValue}
            onChange={onDepartmentChange}
            variant="filled"
            ariaLabel="Department: All"
            className="h-full w-full"
          />
        </div>

        {/* Status Dropdown Control */}
        <div className="h-11 w-full sm:w-[170px] flex-none [&>*]:h-full [&_button]:h-full [&_button]:!rounded-xl">
          <Dropdown
            options={statusOptions}
            value={statusFilter}
            onChange={onStatusFilterChange}
            variant="filled"
            ariaLabel="Status: All"
            className="h-full w-full"
          />
        </div>

        {/* Date Picker Control */}
        <div className="h-11 w-full sm:w-[190px] flex-none [&>*]:h-full [&_button]:h-full [&_button]:!rounded-xl">
          <CustomDatePicker
            value={selectedDate}
            onChange={onDateChange}
            placeholder="Select date"
            className="h-full w-full"
            buttonClassName="h-full w-full !rounded-xl text-sm border-[#dfe7f0] bg-white text-[#111827]"
          />
        </div>

        {/* Export Button Control */}
        <div className="h-11 w-full sm:w-auto flex-none [&>*]:h-full [&_button]:h-full">
          <Button
            variant="secondary"
            size="md"
            icon={<Download className="h-4 w-4" />}
            onClick={onExport}
            disabled={isExporting}
            className="h-full w-full sm:w-auto px-5 !rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs"
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>

        {/* Clear Filters Button Control */}
        <div className="h-11 w-full sm:w-auto flex-none [&>*]:h-full [&_button]:h-full">
          <Button
            variant="secondary"
            size="md"
            onClick={onClearFilters}
            className="h-full w-full sm:w-auto px-5 !rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Row Directly Below: Summary Stats */}
      <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 text-xs font-semibold text-slate-600 flex-wrap">
          <div className="flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200/80 px-3 py-1 rounded-full shadow-2xs">
            <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
            <span>{presentCount} Present</span>
          </div>
          <div className="flex items-center gap-1.5 bg-amber-50 text-amber-800 border border-amber-200/80 px-3 py-1 rounded-full shadow-2xs">
            <span className="h-2 w-2 rounded-full bg-amber-600 shrink-0" />
            <span>{lateCount} Late</span>
          </div>
          <div className="flex items-center gap-1.5 bg-red-50 text-red-800 border border-red-200/80 px-3 py-1 rounded-full shadow-2xs">
            <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
            <span>{absentCount} Absent</span>
          </div>
        </div>
      </div>
    </div>
  )
}
